import type { CommonArea, CommonAreaBooking } from '../../services/commonAreaReservationService';

/**
 * What the portaria's "Nova Reserva" has to know before it sends anything.
 *
 * The operational modal had none of it: it demanded a start and an end time for EVERY area —
 * including the ones a condomínio books by the day — stamped a `Z` onto a local wall clock, and
 * only discovered a slot was taken when the server answered 409. The mobile apps already behave
 * correctly (AreaComumView.swift / AreaComumScreen.kt); these rules are written to match them, in
 * one pure place so the three surfaces can be held to the same answer.
 *
 * See reservaRules.test.ts.
 */

export type BookingMode = 'whole_day' | 'time_slot';

export interface OperatingWindow {
  open: string;
  close: string;
}

/** Statuses that still hold the space. Cancelled/Revoked release it. */
const LIVE_STATUSES = new Set(['Pending', 'Confirmed', 'Approved']);

/**
 * `whole_day` is the default.
 *
 * `booking_mode` arrived with migration 225 and most rows predate it, so absent has to mean the
 * behaviour those areas already had. The apps read it the same way (`!= 'time_slot'`) — reading
 * absent as time_slot would start demanding times for every area a condo books by the day.
 */
export function areaBookingMode(area: Pick<CommonArea, 'booking_mode'>): BookingMode {
  return area.booking_mode === 'time_slot' ? 'time_slot' : 'whole_day';
}

/** The weekday (0=Sun..6=Sat) of a yyyy-mm-dd, read as a LOCAL date. */
function weekdayOf(dateISO: string): number {
  const [y, m, d] = dateISO.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1).getDay();
}

/**
 * When the area is open on that date, or null when it is not open at all.
 *
 * Precedence: the `operating_hours` entry for that weekday, then the area-wide
 * opening/closing times. An EMPTY `available_days_of_week` means every day — the list is a
 * restriction, and an area that never had it filled in is open, not shut.
 */
export function operatingWindowFor(area: CommonArea, dateISO: string): OperatingWindow | null {
  const weekday = weekdayOf(dateISO);
  const allowedDays = area.available_days_of_week ?? [];
  if (allowedDays.length > 0 && !allowedDays.includes(weekday)) return null;

  const perDay = (area.operating_hours ?? []).find(h => h.weekday === weekday);
  if (perDay?.open && perDay?.close) return { open: perDay.open, close: perDay.close };

  const open = area.opening_time || '00:00';
  const close = area.closing_time || '23:59';
  return { open, close };
}

/**
 * The hours to PRINT for a date — "06:00 às 22:00".
 *
 * Deliberately never the words "dia inteiro": a whole-day booking is the space for the day it is
 * open, not twenty-four hours, and a resident who reads "dia inteiro" turns up at seven in the
 * morning to a locked gate. The apps print the window for the same reason.
 */
export function operatingWindowLabel(area: CommonArea, dateISO: string): string {
  const window = operatingWindowFor(area, dateISO);
  if (!window) return 'Fechado neste dia';
  return `${window.open} às ${window.close}`;
}

/** yyyy-mm-dd of a booking timestamp, read as a LOCAL wall clock. */
function localDateOf(timestamp: string): string {
  // Take the date part of an ISO-like string directly when there is one: a 23:00 São Paulo
  // booking is the NEXT day in UTC, so round-tripping through Date would move it.
  const match = /^(\d{4}-\d{2}-\d{2})[T ]/.exec(timestamp);
  if (match) return match[1];
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** HH:MM of a booking timestamp, read as a LOCAL wall clock. */
function localTimeOf(timestamp: string): string {
  const match = /^\d{4}-\d{2}-\d{2}[T ](\d{2}:\d{2})/.exec(timestamp);
  if (match) return match[1];
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function isLive(b: CommonAreaBooking): boolean {
  return LIVE_STATUSES.has(b.status);
}

/**
 * The days that already hold a booking — what the calendar marks, and what a whole-day area
 * refuses outright (one reservation per day is the whole point of that mode).
 */
export function occupiedDatesFrom(bookings: CommonAreaBooking[]): Set<string> {
  const dates = new Set<string>();
  for (const b of bookings) {
    if (!isLive(b)) continue;
    const day = localDateOf(b.start_time);
    if (day) dates.add(day);
  }
  return dates;
}

/** What is taken on one day, in clock times, in the order the day runs. */
export function busyIntervalsFor(
  bookings: CommonAreaBooking[],
  dateISO: string,
): Array<{ start: string; end: string }> {
  return bookings
    .filter(b => isLive(b) && localDateOf(b.start_time) === dateISO)
    .map(b => ({ start: localTimeOf(b.start_time), end: localTimeOf(b.end_time) }))
    .sort((a, b) => a.start.localeCompare(b.start));
}

/**
 * The booking a candidate slot would run into, or null.
 *
 * Half-open [start, end): a booking may begin exactly when another ends, which is how a quadra
 * gets used for four consecutive hours by four different families.
 */
export function conflictFor(
  bookings: CommonAreaBooking[],
  dateISO: string,
  start: string,
  end: string,
): CommonAreaBooking | null {
  for (const b of bookings) {
    if (!isLive(b) || localDateOf(b.start_time) !== dateISO) continue;
    const bStart = localTimeOf(b.start_time);
    const bEnd = localTimeOf(b.end_time);
    if (start < bEnd && end > bStart) return b;
  }
  return null;
}

export interface LocalRangeInput {
  area: CommonArea;
  dateISO: string;
  start?: string;
  end?: string;
}

/**
 * The `start_time`/`end_time` to send: the wall clock the operator chose, suffixed with `Z`.
 *
 * Two mistakes are already buried here, so both are stated.
 *
 * It is not a zoneless `2026-10-21T07:00:00`. The server binds these into a Go `time.Time` through
 * encoding/json, which accepts RFC3339 and nothing else, so a zoneless stamp never reached the
 * database at all — it came back as
 *   `parsing time "2026-10-21T07:00:00" ... cannot parse "" as "Z07:00"` → 400.
 *
 * And it is not the real -03:00 offset either, because every other party treats these timestamps
 * as wall clocks: CreateBooking validates `startTime.Hour()` against the área's opening hours and
 * normalises a whole-day booking to 00:00:00–23:59:59 of `startTime`'s OWN location, while the
 * clients read the booked day and hour out of the string's calendar fields. Sending -03:00 would
 * store a whole-day booking as 03:00Z and break the 00:00→23:59 detection those readers rely on.
 *
 * A whole-day booking spans the area's OPERATING WINDOW for that date rather than 00:00–23:59 —
 * the space is not available at three in the morning, and the window is what any gate credential
 * cut from this booking has to match.
 */
export function localRangeFor({ area, dateISO, start, end }: LocalRangeInput): {
  start_time: string;
  end_time: string;
} {
  if (areaBookingMode(area) === 'whole_day') {
    const window = operatingWindowFor(area, dateISO) ?? { open: '00:00', close: '23:59' };
    return {
      start_time: `${dateISO}T${window.open}:00Z`,
      end_time: `${dateISO}T${window.close}:00Z`,
    };
  }
  return {
    start_time: `${dateISO}T${start}:00Z`,
    end_time: `${dateISO}T${end}:00Z`,
  };
}

/** Minutes since midnight for an HH:MM. */
function minutesOf(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function daysBetween(fromISO: string, toISO: string): number {
  const [fy, fm, fd] = fromISO.split('-').map(Number);
  const [ty, tm, td] = toISO.split('-').map(Number);
  const from = new Date(fy, fm - 1, fd).getTime();
  const to = new Date(ty, tm - 1, td).getTime();
  return Math.round((to - from) / 86_400_000);
}

function localDateStringOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export interface ValidateInput {
  area: CommonArea;
  dateISO: string;
  start: string;
  end: string;
  bookings: CommonAreaBooking[];
  blackoutDates: string[];
  now: Date;
}

/**
 * The reason this reservation cannot be made, in Portuguese, or null when it can.
 *
 * Client-side so the portaria is told BEFORE they finish typing; the server validates all of it
 * again (it is the only party that sees concurrent bookings), so a rule that drifts here can
 * only produce a redundant refusal, never a reservation that should not exist.
 */
export function validateReservation({
  area,
  dateISO,
  start,
  end,
  bookings,
  blackoutDates,
  now,
}: ValidateInput): string | null {
  if (!dateISO) return 'Selecione a data';

  const todayISO = localDateStringOf(now);
  if (dateISO < todayISO) return 'A data não pode ser no passado';

  const window = operatingWindowFor(area, dateISO);
  if (!window) return 'A área não abre neste dia da semana';

  if (blackoutDates.includes(dateISO)) return 'Esta data está bloqueada para reservas (indisponível)';

  const advance = area.max_advance_days ?? 0;
  if (advance > 0 && daysBetween(todayISO, dateISO) > advance) {
    return `Reservas são aceitas com no máximo ${advance} dias de antecedência`;
  }

  const mode = areaBookingMode(area);

  if (mode === 'whole_day') {
    // One booking per day — the apps make such a day unselectable in the calendar.
    if (occupiedDatesFrom(bookings).has(dateISO)) {
      return 'Este dia já possui uma reserva nesta área';
    }
  } else {
    if (!start || !end) return 'Preencha o horário de início e de fim';
    if (end <= start) return 'O horário de início deve ser anterior ao fim';
    if (start < window.open) return `A área abre às ${window.open}`;
    if (end > window.close) return `A área fecha às ${window.close}`;

    const maxHours = area.max_duration_hours ?? 0;
    if (maxHours > 0 && minutesOf(end) - minutesOf(start) > maxHours * 60) {
      return `A duração máxima desta área é de ${maxHours}h`;
    }

    if (conflictFor(bookings, dateISO, start, end)) {
      return 'Este horário já está reservado';
    }
  }

  // Minimum notice, measured from the moment the booking STARTS.
  const minAdvance = area.min_advance_minutes ?? 0;
  if (minAdvance > 0) {
    const startClock = mode === 'whole_day' ? window.open : start;
    const [y, m, d] = dateISO.split('-').map(Number);
    const [h, min] = startClock.split(':').map(Number);
    const startsAt = new Date(y, m - 1, d, h ?? 0, min ?? 0);
    if (startsAt.getTime() - now.getTime() < minAdvance * 60_000) {
      const hours = Math.round(minAdvance / 60);
      return hours >= 1
        ? `Esta área exige ${hours}h de antecedência`
        : `Esta área exige ${minAdvance} minutos de antecedência`;
    }
  }

  return null;
}

export type DayState = 'free' | 'partial' | 'full' | 'blackout' | 'closed' | 'past' | 'beyond';

export interface DayStateInput {
  area: CommonArea;
  dateISO: string;
  bookings: CommonAreaBooking[];
  blackoutDates: string[];
  now: Date;
  /** Hourly areas only: the length being booked, since a day full for 3h may be free for 1h. */
  durationHours?: number;
}

/**
 * What one day of the calendar is — the same question the apps answer per cell.
 *
 * A single state rather than a pile of booleans, because the cell can only be painted one way and
 * the reason it cannot be picked is what the operator has to read. The order is the order a person
 * would say it: it already happened, the space does not open, it is blocked, it is too far ahead,
 * it is taken.
 */
export function dayStateFor({
  area,
  dateISO,
  bookings,
  blackoutDates,
  now,
  durationHours = 1,
}: DayStateInput): DayState {
  if (dateISO < localDateStringOf(now)) return 'past';

  const window = operatingWindowFor(area, dateISO);
  if (!window) return 'closed';

  if (blackoutDates.includes(dateISO)) return 'blackout';

  const advance = area.max_advance_days ?? 0;
  if (advance > 0 && daysBetween(localDateStringOf(now), dateISO) > advance) return 'beyond';

  if (areaBookingMode(area) === 'whole_day') {
    return occupiedDatesFrom(bookings).has(dateISO) ? 'full' : 'free';
  }

  if (suggestedStartTimes(area, dateISO, bookings, durationHours).length === 0) return 'full';
  return busyIntervalsFor(bookings, dateISO).length > 0 ? 'partial' : 'free';
}

/** Only a day that can actually take this reservation may be clicked. */
export function isSelectableDay(state: DayState): boolean {
  return state === 'free' || state === 'partial';
}

/**
 * Why the month has nothing to offer, or null when it has.
 *
 * The portaria opened "Nova Reserva", saw a full month with a legend promising "Disponível", and
 * none of it took a click — because no ÁREA had been chosen yet, and availability is a property OF
 * one. The grid said nothing about that, so the reading was "the system is broken". An área whose
 * weekdays, hours or blackouts leave the whole month closed looks exactly the same from the
 * outside, so it gets a sentence of its own rather than the same silence.
 */
export function noSelectableDaysReason(
  area: CommonArea | null | undefined,
  states: DayState[],
): string | null {
  if (!area) return 'Selecione uma área para ver os dias disponíveis.';
  if (!states.some(isSelectableDay)) return 'Nenhum dia deste mês está disponível para esta área.';
  return null;
}

export interface CalendarDay {
  dateISO: string;
  /** False for the leading/trailing days that only exist to fill the first and last week. */
  inMonth: boolean;
}

/**
 * A month laid out in whole Sunday-to-Saturday weeks — the grid the calendar renders.
 *
 * Built by walking Dates from the Sunday on or before the 1st, so month lengths, leap days and
 * DST are the platform's problem rather than arithmetic here.
 */
export function calendarMonthDays(year: number, monthIndex: number): CalendarDay[] {
  const first = new Date(year, monthIndex, 1);
  const last = new Date(year, monthIndex + 1, 0);

  const cursor = new Date(year, monthIndex, 1 - first.getDay());
  const end = new Date(year, monthIndex, last.getDate() + (6 - last.getDay()));

  const out: CalendarDay[] = [];
  while (cursor <= end) {
    out.push({ dateISO: localDateStringOf(cursor), inMonth: cursor.getMonth() === monthIndex });
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

/**
 * The hours a booking of `durationHours` could still start at — what the portaria picks from
 * instead of typing a time and being told no.
 *
 * Whole hours only: every area in production is configured in whole hours, and a list of
 * quarter-hours is unreadable at a gate.
 */
export function suggestedStartTimes(
  area: CommonArea,
  dateISO: string,
  bookings: CommonAreaBooking[],
  durationHours: number,
): string[] {
  const window = operatingWindowFor(area, dateISO);
  if (!window) return [];

  const duration = Math.max(1, Math.round(durationHours)) * 60;
  const out: string[] = [];
  for (let m = minutesOf(window.open); m + duration <= minutesOf(window.close); m += 60) {
    const start = `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    const endM = m + duration;
    const end = `${String(Math.floor(endM / 60)).padStart(2, '0')}:${String(endM % 60).padStart(2, '0')}`;
    if (!conflictFor(bookings, dateISO, start, end)) out.push(start);
  }
  return out;
}
